import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const Card = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`
const CardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 4px;
`
const Subtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0 0 20px;
`
const EmptyMsg = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0;
  padding: 12px 0;
`
const GridWrapper = styled.div`overflow-x: auto;`
const Grid = styled.div`
  display: grid;
  grid-template-columns: 120px repeat(${({ $cols }) => $cols}, 1fr);
  gap: 6px;
  min-width: 400px;
`
const HeaderEmpty = styled.div``
const HeaderCell = styled.div`
  text-align: center;
  padding: 8px 6px;
  background: ${({ $bg }) => $bg};
  border-radius: 8px;
`
const HeaderLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  text-transform: uppercase;
  letter-spacing: .06em;
  margin: 0;
`
const TierCell = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 0;
`
const TierPill = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
`
const DataCell = styled.div`
  background: ${({ $bg }) => $bg};
  border: 1px solid ${({ $border }) => $border};
  border-radius: 10px;
  padding: 14px 12px;
  text-align: center;
`
const CellCount = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 26px;
  font-weight: 800;
  color: ${({ $color }) => $color};
  margin: 0;
  line-height: 1;
`
const CellLabel = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  color: ${({ $muted }) => $muted ? 'var(--vo-steel)' : 'var(--vo-slate)'};
  margin: 4px 0 0;
`
const Legend = styled.div`
  display: flex;
  gap: 14px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid var(--vo-mist);
  flex-wrap: wrap;
`
const LegendItem = styled.div`display: flex; align-items: center; gap: 6px;`
const LegendDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`
const LegendText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
`

const CLASSIFICATION_CONFIG = {
  'Audit Ready': {
    headerBg: 'var(--vo-success-10)', headerText: 'var(--vo-success)',
    dataBg: 'color-mix(in srgb, var(--vo-success) 8%, transparent)', dataBorder: 'var(--vo-success-15)', dataColor: 'var(--vo-success)',
    legendColor: 'color-mix(in srgb, var(--vo-success) 30%, transparent)', legendKey: 'legendAuditReady',
  },
  'Risky': {
    headerBg: 'var(--vo-heatmap-risky)', headerText: 'var(--vo-warning)',
    dataBg: 'color-mix(in srgb, var(--vo-warning) 8%, transparent)', dataBorder: 'var(--vo-warning-15)', dataColor: 'var(--vo-warning)',
    legendColor: 'color-mix(in srgb, var(--vo-warning) 40%, transparent)', legendKey: 'legendRisky',
  },
  'Unacceptable': {
    headerBg: 'var(--vo-danger-10)', headerText: 'var(--vo-danger)',
    dataBg: 'var(--vo-danger-10)', dataBorder: 'var(--vo-danger-20)', dataColor: 'var(--vo-danger)',
    legendColor: 'color-mix(in srgb, var(--vo-danger) 50%, transparent)', legendKey: 'legendUnacceptable',
  },
}

const TIER_CONFIG = {
  1: { color: 'var(--vo-danger)', bg: 'var(--vo-danger-10)' },
  2: { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' },
  3: { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' },
}

const EMPTY_CELL = { dataBg: 'var(--vo-mist)', dataBorder: 'var(--vo-cloud)', dataColor: 'var(--vo-steel)' }

export const RiskHeatmap = ({ data }) => {
  const { t } = useTranslation(['dashboard'])

  if (!data || !data.tiers || !data.classifications || !data.data) {
    return (
      <Card>
        <CardTitle>{t('dashboard:riskHeatmap.noDataTitle')}</CardTitle>
        <EmptyMsg>{t('dashboard:riskHeatmap.noData')}</EmptyMsg>
      </Card>
    )
  }

  const { tiers, classifications } = data

  const getCell = (tier, classification) =>
    data.data.find(d => d.tier === tier && d.classification === classification)

  const getTierLabel = (tier) => {
    switch (tier) {
      case 1: return t('dashboard:riskHeatmap.tier1')
      case 2: return t('dashboard:riskHeatmap.tier2')
      case 3: return t('dashboard:riskHeatmap.tier3')
      default: return `Tier ${tier}`
    }
  }

  return (
    <Card>
      <CardTitle>{t('dashboard:riskHeatmap.title')}</CardTitle>
      <Subtitle>{t('dashboard:riskHeatmap.subtitle')}</Subtitle>

      <GridWrapper>
        <Grid $cols={classifications.length}>
          <HeaderEmpty />
          {classifications.map((cls) => {
            const cfg = CLASSIFICATION_CONFIG[cls] || { headerBg: 'var(--vo-mist)', headerText: 'var(--vo-slate)' }
            return (
              <HeaderCell key={cls} $bg={cfg.headerBg}>
                <HeaderLabel $color={cfg.headerText}>{cls}</HeaderLabel>
              </HeaderCell>
            )
          })}

          {tiers.map((tier) => {
            const tierCfg = TIER_CONFIG[tier] || { color: 'var(--vo-slate)', bg: 'var(--vo-mist)' }
            return (
              <React.Fragment key={tier}>
                <TierCell>
                  <TierPill $color={tierCfg.color} $bg={tierCfg.bg}>
                    {getTierLabel(tier)}
                  </TierPill>
                </TierCell>
                {classifications.map((cls) => {
                  const cell = getCell(tier, cls)
                  const count = cell?.count || 0
                  const cfg = count > 0 ? (CLASSIFICATION_CONFIG[cls] || EMPTY_CELL) : EMPTY_CELL
                  return (
                    <DataCell key={`${tier}-${cls}`} $bg={cfg.dataBg} $border={cfg.dataBorder}>
                      <CellCount $color={cfg.dataColor}>{count}</CellCount>
                      <CellLabel $muted={count === 0}>
                        {count === 1 ? t('dashboard:riskHeatmap.vendor') : t('dashboard:riskHeatmap.vendors')}
                      </CellLabel>
                    </DataCell>
                  )
                })}
              </React.Fragment>
            )
          })}
        </Grid>
      </GridWrapper>

      <Legend>
        {classifications.map((cls) => {
          const cfg = CLASSIFICATION_CONFIG[cls]
          if (!cfg) return null
          return (
            <LegendItem key={cls}>
              <LegendDot $color={cfg.legendColor} />
              <LegendText>{t(`dashboard:riskHeatmap.${cfg.legendKey}`)}</LegendText>
            </LegendItem>
          )
        })}
      </Legend>
    </Card>
  )
}
